import React from "react";
import { 
  Grid, 
  Typography, 
  Box, 
  Paper,
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  Avatar,
  useTheme
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Link } from "react-router-dom";

// Simple Stat Card
const StatCard = ({ icon, title, value, trend, trendValue, color }) => {
  const theme = useTheme();
  
  return (
    <Card variant="outlined" sx={{ 
      height: '100%', 
      borderRadius: 1, 
      boxShadow: 'none',
      backgroundColor: 'white'
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Avatar 
            sx={{ 
              bgcolor: theme.palette.grey[100], 
              color: color || theme.palette.primary.main,
              width: 36, 
              height: 36 
            }}
          >
            {icon}
          </Avatar>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1.5 }}>
            {title}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
            {value}
          </Typography>
          
          {trend && trendValue && (
            <Typography 
              variant="body2" 
              color={trend === 'up' ? 'success.main' : 'error.main'} 
              sx={{ ml: 1, fontWeight: 'medium' }}
            >
              {trend === 'up' && '↑'} {trendValue}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// Simple Section Card
const SectionCard = ({ title, children }) => {
  return (
    <Card 
      variant="outlined" 
      sx={{ 
        height: '100%', 
        borderRadius: 1, 
        boxShadow: 'none',
        backgroundColor: 'white'
      }}
    >
      <CardHeader 
        title={
          <Typography variant="h6" component="h2">
            {title}
          </Typography>
        }
        sx={{ 
          p: 3, 
          pb: 1.5,
          borderBottom: 'none'
        }}
      />
      <CardContent sx={{ pt: 0, px: 3, pb: 3 }}>
        {children}
      </CardContent>
    </Card>
  );
};

// Appointment Item
const AppointmentItem = ({ name, type, date, time, status }) => {
  const theme = useTheme();
  
  return (
    <Box 
      sx={{ 
        py: 3, 
        borderRadius: 1,
        borderBottom: '1px solid',
        borderColor: theme.palette.grey[100]
      }}
    >
      <Grid container alignItems="center">
        {/* Left side - Avatar and name/type */}
        <Grid item xs={5} sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            sx={{ 
              bgcolor: theme.palette.primary[50] || theme.palette.grey[100], 
              color: theme.palette.primary.main,
              width: 40, 
              height: 40
            }}
          >
            <PersonIcon fontSize="small" />
          </Avatar>
          
          <Box sx={{ ml: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
              {name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {type}
            </Typography>
          </Box>
        </Grid>
        
        {/* Right side - Date and time */}
        <Grid item xs={7} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
            <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2">
              {date}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
            <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2">
              {time}
            </Typography>
          </Box>
          
          <Box 
            sx={{ 
              px: 3, 
              py: 0.5, 
              borderRadius: 5,
              backgroundColor: theme.palette.primary[50] || theme.palette.grey[100],
              color: theme.palette.primary.main
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
              {status}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// Activity Item
const ActivityItem = ({ name, action, time }) => {
  return (
    <Box 
      sx={{ 
        py: 2, 
        display: 'flex', 
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'grey.100'
      }}
    >
      <Avatar sx={{ bgcolor: 'grey.100', width: 36, height: 36 }}>
        <PersonIcon fontSize="small" sx={{ color: 'grey.500' }} />
      </Avatar>
      
      <Box sx={{ ml: 2 }}>
        <Typography variant="body2">
          <Box component="span" sx={{ fontWeight: 'medium' }}>
            {name}
          </Box>{" "}
          {action}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {time}
        </Typography>
      </Box>
    </Box>
  );
};

// Main Dashboard Component
const Dashboard = () => {
  // Mock data
  const upcomingAppointments = [
    {
      id: "1",
      name: "Sarah Johnson",
      type: "Therapy Session",
      date: "Today",
      time: "2:00 PM",
      status: "Upcoming",
    },
    {
      id: "2",
      name: "Michael Chen",
      type: "Initial Consultation",
      date: "Today",
      time: "4:30 PM",
      status: "Upcoming",
    },
    {
      id: "3",
      name: "Emma Wilson",
      type: "Follow-up",
      date: "Tomorrow",
      time: "10:00 AM",
      status: "Upcoming",
    },
  ];

  const activities = [
    { name: "Sarah Johnson", action: "completed assessment", time: "2 hours ago" },
    { name: "John Davis", action: "confirmed appointment", time: "Yesterday" },
    { name: "Emma Wilson", action: "rescheduled session", time: "Yesterday" },
    { name: "Michael Chen", action: "paid invoice #1234", time: "2 days ago" },
  ];

  return (
    <Box sx={{ px: 2, py: 3, bgcolor: 'grey.50', minHeight: '100vh' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: "bold", mb: 1 }}>
          Welcome Back, Dr. Rachel
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Here's your practice overview for today
        </Typography>
      </Box>
      
      {/* Stats Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Total Clients" 
            value="42" 
            trend="up"
            trendValue="3 this month"
            icon={<PeopleIcon fontSize="small" />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Today's Sessions" 
            value="6"
            icon={<CalendarMonthIcon fontSize="small" />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Monthly Revenue" 
            value="$8,245"
            trend="up"
            trendValue="12% vs last month"
            icon={<TrendingUpIcon fontSize="small" />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Pending Payments" 
            value="$1,350"
            icon={<CreditCardIcon fontSize="small" />}
            color="primary.main"
          />
        </Grid>
      </Grid>
      
      <Grid container spacing={2}>
        {/* Upcoming Appointments Section */}
        <Grid item xs={12} md={7}>
          <SectionCard title="Upcoming Appointments">
            {upcomingAppointments.map(appointment => (
              <AppointmentItem 
                key={appointment.id}
                name={appointment.name}
                type={appointment.type}
                date={appointment.date}
                time={appointment.time}
                status={appointment.status}
              />
            ))}
            
            <Box sx={{ mt: 2, textAlign: 'right' }}>
              <Link 
                to="/scheduler" 
                style={{ 
                  color: '#1976d2', 
                  textDecoration: 'none',
                  fontSize: '0.875rem'
                }}
              >
                View All Appointments →
              </Link>
            </Box>
          </SectionCard>
        </Grid>
        
        {/* Recent Client Activity Section */}
        <Grid item xs={12} md={5}>
          <SectionCard title="Recent Client Activity">
            {activities.map((activity, index) => (
              <ActivityItem 
                key={index}
                name={activity.name}
                action={activity.action}
                time={activity.time}
              />
            ))}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;